/**
 * Renaming a spawn code everywhere it is actually a reference, and nowhere else.
 *
 * A spawn code is not one string in one file. `20ambocr` is the `<modelName>` a
 * server owner types into `/car`, and it is also — when the pack follows the
 * ordinary conventions — the `<txdName>`, the `<handlingId>`, the `handling.meta`
 * entry that id points at, the stem of four files under `stream/`, and sometimes
 * a key inside a Lua table a framework reads. Renaming only the file the user
 * clicked and calling it done is how a vehicle keeps its old physics, loses its
 * mods, or disappears from a shop menu while the tool that did it reports success.
 *
 * The competitor this replaces renames every `.meta` and every stream file it can
 * find and calls the job finished. Two things follow from doing that blindly, and
 * the research's own run hit both: it rewrote an *unrelated* resource's
 * `<handlingId>` because that resource's field happened to equal another pack's
 * model name, and — the one that actually destroys data — on a name collision
 * (two codes swapped) it overwrote one file with another and reported
 * `Done. Downloaded a renamed pack.`
 *
 * So this module is built around two ideas that are each a direct answer to one
 * of those failures.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 1. SCOPE, NOT SEARCH. Nothing here greps a file for a whole-word match. Every
 *    edit starts from a *reference*: `analyzeVehicleMeta`'s own graph of which
 *    vehicle owns which `handlingId`, which `carvariations.meta` row owns which
 *    kit, which resource owns which `vehicles.meta`. A field is only a candidate
 *    for editing once it is known to belong to *this* vehicle, in *this*
 *    resource — an unrelated resource's `<handlingId>` is never so much as read
 *    for the codes it does not itself declare in `vehicles.meta`, which is what
 *    stops the exact defect above from recurring.
 *
 *    Inside a field that *is* in scope, the match is still a whole word, and
 *    `\b` already does the right thing here because `\w` includes `_`: a rename
 *    of `20ambocr` leaves `4826_20ambocr_modkit` and `20ambocrew` untouched,
 *    because there is no word boundary on either side of an underscore or
 *    between two letters. That is not a special case coded in — every field this
 *    module touches is tested the same way, so a modkit id that embeds a spawn
 *    code as a compound identifier survives for free, and so does a different
 *    vehicle whose name happens to start the same way.
 *
 * 2. PLAN, THEN APPLY, AND REFUSE WHOLESALE. `planRename` never writes anything;
 *    it returns a `RenamePlan` a caller can show, edit, or throw away. Every edit
 *    it produces goes through `textEdit` from `patch.ts`, which is the point:
 *    each one carries the exact text it was calculated against, so if anything
 *    about the file has changed by the time a caller actually applies the plan,
 *    `applyEdits`' verify step refuses that edit rather than splicing it into the
 *    wrong place. This module never bypasses that check to save a call — there is
 *    no code path here that writes a byte without going through `verifyEdit`.
 *
 *    Collisions are computed before a caller can write any of that, in two
 *    passes that cover two different namespaces, because one pass cannot:
 *
 *      • **Names.** The set of spawn codes every resource in the call would end
 *        up owning — declared `<modelName>`s and `.yft`/`.ytd` stems, existing
 *        names included — is built once, up front. Two renames colliding, a
 *        rename colliding with a vehicle already in the pack, a rename colliding
 *        with another pack in the same call, or a rename colliding with one of
 *        the 238,359 names the base game itself owns all land here.
 *
 *      • **Paths.** The set of *output paths* every file in the call would be
 *        written to, after the renames this plan produced. This is the check the
 *        brief's decision #2 actually asks for, and it is a separate pass
 *        because the name pass structurally cannot see it: eleven of the
 *        thirteen extensions this module renames carry no name it can read, and
 *        the `sfx/dlc_<code>/` folder convention carries none at all. Without
 *        it, renaming a vehicle that ships `<code>.ytyp` into a pack that
 *        already has a `<new>.ytyp` produced a plan reporting `blocked: false`
 *        and `collisions: []` whose own export wrote two zip entries with one
 *        path — destroying a file and reporting success, which is the precise
 *        failure this module was written to replace.
 *
 *    Any entry from either pass populates `collisions`, and `fileRenames` and
 *    `edits` both come back empty. A two-way swap survives both passes without a
 *    special case: A→B and B→A is two sources resolving to two distinct targets,
 *    in the name space and in the path space alike.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHERE THIS DIFFERS FROM `Research/Claude/server-tools/tools/spawn-rename.md`
 *
 * The brief sketches `planRename(drop: ResourceDrop, ...)`. A `ResourceDrop` is
 * `intake.ts`'s output — folders and zips that have been inventoried but not
 * read, whose text still sits behind `File.text()` and an `await`. Every other
 * analyzer in this layer (`analyzeVehicleMeta`, `buildSpawnIndex`, `analyzeBudget`)
 * takes `GtaResourceInput[]` instead — the *collected*, synchronous shape, with
 * `texts` already decoded — and this module shares its parsing with
 * `spawnIndex.ts`, which is one of them. Taking `ResourceDrop` here would make
 * this the one analyzer in the layer that is async and the one that re-reads
 * files a caller may have already read for the spawn index two clicks earlier.
 * So `planRename` takes `readonly GtaResourceInput[]`, matching the rest of the
 * layer; the later page that builds on this module runs intake and
 * `collectResources` first, the same as every other free tool does.
 *
 * The brief also calls a rename that lands on a base-game name "a warning before
 * the run". The task this module was built against overrides that: colliding
 * with `police` is a real, silent, in-game failure — the custom vehicle replaces
 * the base-game one everywhere, forever, for every player — and a warning is
 * exactly the sentence a user skims past on the way to the button the competitor
 * shipped. So a vanilla-name target is a `collisions` entry here, refused with
 * the rest, not a warning beside a live "rename" button.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LIMITS — read this before trusting a clean plan
 *
 * `planRename` renames a code inside `vehicles.meta`, `handling.meta`,
 * `carvariations.meta`, `carcols.meta`, `vehicle_names.lua`, a QBCore-style
 * `shared/vehicles.lua` or an ESX `vehicles.sql` when one of those is in the
 * drop, the streamed model/texture/anim/collision files `STREAM_EXTENSIONS`
 * already knows about (which is where `.ytyp`, `.ymt` and `.ynv` come from, not
 * a second list invented here), their high-LOD partners, and an `sfx/dlc_<code>/`
 * folder. Every one of those is a text or a path, which is what `patch.ts` and a
 * `{from, to}` pair can safely describe.
 *
 * It does **not** rewrite the *contents* of a `.gxt2`, a `.dat`/`.rel` audio
 * bank, or a `.ytyp`'s own internal archetype names. Those are compiled or
 * structured binary formats this layer has no parser for, and guessing at a byte
 * layout to save a user one manual edit is a worse trade than telling them
 * plainly that it was not attempted. Every plan says so in `notes` whenever one
 * of those files is present, by name, rather than staying silent about a file it
 * walked past.
 *
 * An audio bank's *file name* is a path, not a byte layout, so that much is
 * renamed — but only when the stem spells the code as a whole word, through the
 * same `wordSpans` rule every field here goes through. `20ambocr.dat151.rel`
 * matches (a `.` is a word boundary); `dlcambo_game.dat` does not, because
 * nothing in that string is `20ambocr`. The note is explicit about which of the
 * two happened to each bank in the drop and counts them, rather than stating a
 * rule and leaving the user to assume it fired. Renaming the file does not
 * re-point a vehicle's `audioNameHash` at a different bank — the hash names an
 * entry inside the compiled file, which stays unread — and the note says that
 * too. A `.gxt2` is neither opened nor renamed: it is named after a language,
 * not a vehicle.
 *
 * Only whole-file renames get a matching `fxmanifest.lua`/`__resource.lua`
 * update — the plan searches the manifest text for the exact old path, quoted,
 * and edits it alongside the file. It does not update the *contents* of another
 * script that happens to reference a renamed path by string; scanning arbitrary
 * Lua for a path literal is a different, much noisier tool.
 */

import { MANIFEST_FILE_NAMES } from "./analyze";
import { disambiguateLabels, EM_DASH_LABEL_STYLE } from "./labels";
import { MANIFEST_DECLARED_EXTENSIONS, STREAM_EXTENSIONS } from "./data/dataFileKinds.v1";
import { VANILLA_VEHICLE_MODELS } from "./data/vanillaVehicles.v1";
import {
  baseName,
  extensionOf,
  highLodNames,
  invalidSpawnNameChars,
  isHighLodName,
  isInStreamFolder,
  isValidSpawnName,
  normalizeName,
  normalizePath,
  stemOf,
  stripHighLod,
} from "./normalize";
import { applyEdits, textEdit } from "./patch";
import {
  metaKindOf,
  parseCarcolsMeta,
  parseCarvariationsMeta,
  parseHandlingMeta,
  parseMetaXml,
  parseVehiclesMeta,
} from "./vehicleMeta";
import type { GtaFinding, GtaResourceInput, GtaTextEdit, VanillaAssetNameIndex } from "./types";

// ─── Public shape ────────────────────────────────────────────────────────────

/** Who made a plan, for the manifest a caller writes into an exported zip. */
export const RENAME_TOOL = "The Workshop — Spawn Rename";

/** Two sources resolving to one target. Every entry here blocks the whole run. */
export type RenameCollision = {
  /** The normalized name two or more things would end up owning. */
  target: string;
  /** What already claims it, or would after this batch — one sentence each. */
  sources: string[];
};

/** A requested new name this module refuses to write anywhere, and why. */
export type RenameValidationIssue = {
  code: string;
  to: string;
  reason: string;
};

/** One field this plan changed or would change, for a per-code summary. */
export type RenamedEntry = {
  code: string;
  to: string;
  resource: string;
  identity: string;
};

export type RenamePlan = {
  /** Every whole-file rename this pack needs, resource-qualified paths both sides. */
  fileRenames: { from: string; to: string }[];
  /** Every in-place text edit, ready for `applyEdits`. `file` is resource-qualified. */
  edits: GtaTextEdit[];
  /** Any entry here means `fileRenames` and `edits` above are both empty. */
  collisions: RenameCollision[];
  /** Requested codes that matched nothing anywhere in the call. */
  unreferenced: string[];
  /** The blocking facts above, restated as findings a page can hand to `FindingsList`. */
  warnings: GtaFinding[];
  /** Requested new names refused for their own sake — bad characters, too long. */
  invalid: RenameValidationIssue[];
  /**
   * Everything else worth saying that is not a finding and not a collision: a
   * no-op rename, a leading-digit note, a file this module walked past because
   * its format is not one `patch.ts` can safely edit. Never silent.
   */
  notes: string[];
  /** `true` exactly when `collisions.length > 0 || invalid.length > 0`. */
  blocked: boolean;
  /** What this plan does, one row per code that matched something. Informational. */
  renamed: RenamedEntry[];
};

export type RenameOptions = {
  /** The stock asset name index, when the caller loaded it. See `SpawnIndexOptions`. */
  vanillaNames?: VanillaAssetNameIndex;
};

// ─── Limits ──────────────────────────────────────────────────────────────────

/**
 * No official ceiling is published anywhere in the format. Every stock name is
 * well under 20 characters, and 64 is comfortably past every real custom name
 * this layer's fixtures or the research's sample packs contain while still
 * refusing the 200-character string the competitor's own report says it let
 * through without a word.
 */
const MAX_SPAWN_NAME_LENGTH = 64;

/** Lua files this module scans as free text for a whole-word match, by exact basename. */
const VEHICLE_NAMES_LUA = "vehicle_names.lua";

/** A path ending like this is treated as a QBCore-style vehicle table, wherever it sits. */
const QBCORE_VEHICLES_SUFFIX = "shared/vehicles.lua";

/** An ESX vehicle table, by exact basename. */
const ESX_VEHICLES_SQL = "vehicles.sql";

/**
 * The audio-bank extensions whose *file name* this module renames.
 *
 * `MANIFEST_DECLARED_EXTENSIONS` minus `gxt2`, and the subtraction is the point:
 * all three load because a manifest line declares them, but only the two audio
 * ones are conventionally named after the vehicle
 * (`audioconfig/20ambocr.dat151.rel`). A `.gxt2` is named after a language
 * (`american.gxt2`), so renaming one by a spawn code would be a rename nobody
 * asked for. Derived from the shared list rather than typed out, so that a new
 * declared extension appearing there is a compile-time decision here and not a
 * silent omission.
 *
 * The *contents* of these files stay unread — they are compiled binary — which
 * is what the note this module pushes for every one of them says out loud.
 */
const AUDIO_BANK_EXTENSIONS = new Set(
  MANIFEST_DECLARED_EXTENSIONS.filter((extension) => extension !== "gxt2"),
);

// ─── Small shared building blocks ───────────────────────────────────────────

/** Every regex-special character escaped, `+` included — `police+hi.ytd` is real. */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * `identity/path` — the spelling every qualified path and file in this module
 * uses, matching `spawnIndex.ts`'s `qualifyPath` so a row on one surface and a
 * finding about it on another are never talking about two different `police`s.
 */
function qualifyPath(identity: string, path: string): string {
  return `${identity}/${path}`;
}

/**
 * Every non-overlapping whole-word match of `code` in `text`, case-insensitive.
 *
 * `\b` already treats `_` as a word character in JavaScript's regex engine —
 * that is not configured here, it is what `\w` has always meant — which is the
 * entire reason `4826_20ambocr_modkit` never matches a search for `20ambocr`:
 * there is no boundary between a letter and the underscore next to it. This is
 * the one matching rule every field in this module goes through, on purpose:
 * one algorithm the reader can hold in their head, not a special case per tag.
 */
function wordSpans(text: string, code: string): { start: number; end: number }[] {
  if (text === "" || code === "") return [];
  const pattern = new RegExp(`\\b${escapeRegExp(code)}\\b`, "gi");
  const spans: { start: number; end: number }[] = [];
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    spans.push({ start: match.index, end: match.index + match[0].length });
    if (match[0].length === 0) pattern.lastIndex += 1;
  }
  return spans;
}

/**
 * Where the *value* of an XML tag sits in the source, searching forward from
 * `from`.
 *
 * A deliberate, small duplicate of `tagOffset` in `vehicleMeta.ts`, which is not
 * exported: that module's copy searches for a value already known to belong to a
 * live DOM element (comments are never part of a parsed tree), and every caller
 * here passes a value read the same way — through `parseVehiclesMeta`,
 * `parseHandlingMeta`, `parseCarvariationsMeta` or `parseCarcolsMeta`, none of
 * which see a commented-out `<Item>` at all. That is what keeps a code that
 * exists only inside an XML comment out of every edit this module writes: it is
 * never looked up here, because it was never returned by a parser in the first
 * place.
 */
function tagValueOffset(source: string, tag: string, value: string, from = 0): number {
  if (value === "") return -1;
  const name = escapeRegExp(tag);
  const text = escapeRegExp(value);
  const patterns = [
    new RegExp(`(<${name}\\b[^>]*>\\s*)${text}\\s*</`, "i"),
    new RegExp(`(<${name}\\b[^>]*value\\s*=\\s*["'])${text}["']`, "i"),
  ];
  const start = Math.max(0, from);
  const window = source.slice(start);
  for (const pattern of patterns) {
    const match = pattern.exec(window);
    if (match) return start + match.index + match[1].length;
  }
  return -1;
}

/** A direct child's raw value, the way `vehicleMeta.ts`'s private `childText` reads one. */
function directChildText(element: Element, tag: string): string | null {
  for (const child of Array.from(element.children)) {
    if (child.tagName !== tag) continue;
    const raw = (child.getAttribute("value") ?? child.textContent ?? "").trim();
    return raw === "" ? null : raw;
  }
  return null;
}

/**
 * Build the edits for one field, scoped to the exact value this caller already
 * knows belongs to it.
 *
 * The value is located once with `tagValueOffset`, and every whole-word match of
 * `oldCode` *inside that value* becomes its own edit — plural, because a field
 * carries exactly one value but that value can theoretically repeat a word
 * (nothing in this layer's real packs does, but nothing here assumes it can't).
 * `cursor` both bounds the search and advances past it, so two vehicles in one
 * file that happen to share a field value are edited in document order instead
 * of both edits landing on the first occurrence.
 */
function fieldEdits(
  file: string,
  source: string,
  tag: string,
  value: string | null,
  cursor: { at: number },
  oldCode: string,
  newCode: string,
): GtaTextEdit[] {
  if (value === null || value === "") return [];
  const offset = tagValueOffset(source, tag, value, cursor.at);
  if (offset < 0) return [];
  cursor.at = offset + value.length;
  const edits: GtaTextEdit[] = [];
  for (const span of wordSpans(value, oldCode)) {
    const start = offset + span.start;
    const end = offset + span.end;
    edits.push(textEdit(file, start, end, source.slice(start, end), newCode));
  }
  return edits;
}

// ─── Reading what a resource already has ────────────────────────────────────

type MetaDoc = { path: string; source: string; doc: Document };

/** Every `.meta`/`.xml` in a resource whose root marks it as one specific kind. */
function metaFilesOfKind(
  input: GtaResourceInput,
  kind: "vehicles" | "handling" | "carvariations" | "carcols",
): MetaDoc[] {
  const out: MetaDoc[] = [];
  for (const [path, source] of input.texts) {
    const extension = extensionOf(path);
    if (extension !== "meta" && extension !== "xml") continue;
    const doc = parseMetaXml(source);
    if (!doc || metaKindOf(doc) !== kind) continue;
    out.push({ path, source, doc });
  }
  return out.sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
}

/** Every name this resource already answers to: declared models plus stream stems. */
function existingCodesOf(input: GtaResourceInput): Map<string, string> {
  const codes = new Map<string, string>();
  for (const meta of metaFilesOfKind(input, "vehicles")) {
    for (const row of parseVehiclesMeta(meta.doc)) {
      if (row.model === "") continue;
      if (!codes.has(row.model)) codes.set(row.model, meta.path);
    }
  }
  for (const file of input.files ?? []) {
    if (!file || typeof file.path !== "string") continue;
    if (!isInStreamFolder(file.path)) continue;
    const extension = extensionOf(file.path);
    if (extension !== "yft" && extension !== "ytd") continue;
    const stem = normalizeName(stripHighLod(stemOf(file.path)));
    if (stem === "") continue;
    if (!codes.has(stem)) codes.set(stem, file.path);
  }
  return codes;
}

/** The label a collision sentence names a resource by. */
type Labelled = { identity: string; label: string };

function labelResources(resources: readonly GtaResourceInput[]): Labelled[] {
  const headings = disambiguateLabels(
    resources.map((input) => ({ name: input.name, identity: input.identity, origin: input.origin })),
    EM_DASH_LABEL_STYLE,
  );
  return resources.map((input, index) => ({
    identity: input.identity ?? input.name,
    label: headings[index] ?? input.name,
  }));
}

// ─── Validating a requested new name ────────────────────────────────────────

function validateNewCode(oldCode: string, newCode: string, notes: string[]): string | null {
  const trimmed = newCode.trim();
  if (trimmed === "") return "The new name is empty.";
  if (!isValidSpawnName(trimmed)) {
    const bad = invalidSpawnNameChars(trimmed);
    const chars = bad.length > 0 ? bad.join(" ") : "characters the game's name hash cannot represent";
    return (
      `"${newCode}" contains ${chars}, which the engine cannot hash into a working spawn name. ` +
      "Use only letters, digits and underscores."
    );
  }
  if (trimmed.length > MAX_SPAWN_NAME_LENGTH) {
    return (
      `"${newCode}" is ${trimmed.length} characters long, past the ${MAX_SPAWN_NAME_LENGTH}-character ` +
      "ceiling this tool enforces so a name that would break a fixed-size buffer somewhere " +
      "downstream is refused here instead of on someone else's server."
    );
  }
  if (/^[0-9]/.test(trimmed)) {
    notes.push(
      `"${newCode}" starts with a digit. The game accepts that, but some Lua frameworks trip over ` +
        "using it as a bare table key or identifier — quote it everywhere you reference it by hand.",
    );
  }
  if (normalizeName(trimmed) === normalizeName(oldCode)) {
    notes.push(`"${oldCode}" was asked to rename to itself; nothing was done for it.`);
    return "noop";
  }
  return null;
}

// ─── The plan ────────────────────────────────────────────────────────────────

/**
 * Work out everything a set of spawn-code renames would touch, without writing
 * any of it.
 *
 * `resources` is every resource the caller wants renames considered against —
 * usually the whole drop, so that a rename can be refused for colliding with a
 * *different* pack in the same folder. `renames` maps an old code to a new one;
 * a code is looked for in every resource that declares it, so renaming the same
 * code in two packs that both ship it renames it in both — which is the
 * documented behaviour for the one case where that is exactly what a user who
 * typed one old name and one new name means.
 */
export function planRename(
  resources: readonly GtaResourceInput[],
  renames: ReadonlyMap<string, string>,
  options: RenameOptions = {},
): RenamePlan {
  const all = (resources ?? []).filter((input): input is GtaResourceInput => Boolean(input));
  const labels = labelResources(all);
  const labelOf = new Map(labels.map((l) => [normalizeName(l.identity), l.label]));

  const notes: string[] = [];
  const invalid: RenameValidationIssue[] = [];
  const warnings: GtaFinding[] = [];
  const edits: GtaTextEdit[] = [];
  const fileRenames: { from: string; to: string }[] = [];
  const renamed: RenamedEntry[] = [];
  const matchedOld = new Set<string>();

  /** normalized old code -> validated new code, or `null` when it was refused/no-op. */
  const plan = new Map<string, string | null>();
  /** normalized old code -> the spelling the caller actually typed, for messages. */
  const rawOldByNormalized = new Map<string, string>();
  for (const [rawOld, rawNew] of renames) {
    const oldCode = normalizeName(rawOld);
    if (oldCode === "") continue;
    rawOldByNormalized.set(oldCode, rawOld);
    const reason = validateNewCode(rawOld, rawNew, notes);
    if (reason === "noop") {
      plan.set(oldCode, null);
      continue;
    }
    if (reason !== null) {
      invalid.push({ code: rawOld, to: rawNew, reason });
      plan.set(oldCode, null);
      continue;
    }
    plan.set(oldCode, rawNew.trim());
  }

  // ── Occupancy: who ends up owning which name, across the whole call ────────
  //
  // Built once, before a single edit is produced, which is what "refuse on
  // collision" means in practice: the answer to "does this collide" cannot
  // depend on the order resources happen to be iterated in.
  const occupancy = new Map<string, string[]>();
  const claim = (target: string, source: string): void => {
    occupancy.set(target, [...(occupancy.get(target) ?? []), source]);
  };

  const existingByIdentity = new Map<string, Map<string, string>>();
  for (const input of all) {
    const identity = input.identity ?? input.name;
    const existing = existingCodesOf(input);
    existingByIdentity.set(identity, existing);
    const label = labelOf.get(normalizeName(identity)) ?? input.name;
    for (const [code, source] of existing) {
      // Vacated only when this exact resource declares this exact code and a
      // rename for it was requested — a code this resource does not have is
      // never removed from the pool just because some other pack is renaming it.
      const to = plan.get(code);
      if (to !== undefined && to !== null) continue;
      claim(code, `${label}'s ${source}`);
    }
  }

  for (const input of all) {
    const identity = input.identity ?? input.name;
    const label = labelOf.get(normalizeName(identity)) ?? input.name;
    const existing = existingByIdentity.get(identity) ?? new Map<string, string>();
    for (const [oldCode, to] of plan) {
      if (to === null) continue;
      if (!existing.has(oldCode)) continue;
      matchedOld.add(oldCode);
      claim(normalizeName(to), `${label}'s new name for ${oldCode}`);
    }
  }

  const vanilla = new Set(VANILLA_VEHICLE_MODELS);
  for (const [, to] of plan) {
    if (to === null) continue;
    const normalized = normalizeName(to);
    const stock =
      vanilla.has(normalized) ||
      (() => {
        try {
          return options.vanillaNames?.hasBasename(to, "yft") === true;
        } catch {
          return false;
        }
      })();
    if (stock) claim(normalized, "the base game's own vehicle");
  }

  const collisions: RenameCollision[] = [];
  for (const [target, sources] of occupancy) {
    if (sources.length < 2) continue;
    collisions.push({ target, sources: [...new Set(sources)] });
  }
  for (const collision of collisions) {
    warnings.push({
      code: "vehicle.model.duplicate",
      severity: "error",
      title: `"${collision.target}" would belong to more than one thing`,
      detail:
        `${collision.sources.join(" and ")} would all be "${collision.target}" if this batch ran. ` +
        "The game keeps one model per name, so this rename was refused before anything was " +
        "written — rename to a name nothing else in this call already owns.",
      entity: { kind: "vehicle", id: collision.target },
    });
  }

  // ── Per resource: the actual edits and file renames ────────────────────────
  for (const input of all) {
    const identity = input.identity ?? input.name;
    const qualify = (path: string): string => qualifyPath(identity, path);

    // vehicles.meta: the field this vehicle's identity is anchored on, plus the
    // fields decision #1 names — every one of them boundary-tested, never
    // assumed equal to the model just because that is the common convention.
    //
    // Keyed by the *normalized handlingId text*, so two vehicles that legitimately
    // share one handling entry both look it up correctly; the value keeps the
    // vehicle's own old code, which is what the boundary test against
    // `handlingName` below has to run against, not the handlingId text itself.
    const handlingLookup = new Map<string, { oldCode: string; to: string }>();
    for (const meta of metaFilesOfKind(input, "vehicles")) {
      const cursor = { at: 0 };
      for (const row of parseVehiclesMeta(meta.doc)) {
        const oldCode = row.model;
        const to = plan.get(oldCode);
        if (to === undefined || to === null) continue;

        const file = qualify(meta.path);
        edits.push(...fieldEdits(file, meta.source, "modelName", row.modelRaw, cursor, oldCode, to));
        edits.push(...fieldEdits(file, meta.source, "txdName", row.txdName, cursor, oldCode, to));
        edits.push(
          ...fieldEdits(file, meta.source, "handlingId", row.handlingId, cursor, oldCode, to),
        );
        edits.push(...fieldEdits(file, meta.source, "gameName", row.gameName, cursor, oldCode, to));
        edits.push(
          ...fieldEdits(file, meta.source, "audioNameHash", row.audioNameHash, cursor, oldCode, to),
        );
        renamed.push({ code: oldCode, to, resource: input.name, identity });

        if (row.handlingId && wordSpans(row.handlingId, oldCode).length > 0) {
          handlingLookup.set(normalizeName(row.handlingId), { oldCode, to });
        }
      }
    }

    // handling.meta: only the entry the vehicle above actually points at, found
    // by the *old* handlingId — never a document-wide scan for the code.
    for (const meta of metaFilesOfKind(input, "handling")) {
      const cursor = { at: 0 };
      for (const entry of parseHandlingMeta(meta.doc)) {
        const match = handlingLookup.get(normalizeName(entry.handlingName));
        if (!match) continue;
        edits.push(
          ...fieldEdits(
            qualify(meta.path),
            meta.source,
            "handlingName",
            entry.handlingName,
            cursor,
            match.oldCode,
            match.to,
          ),
        );
      }
    }

    // carvariations.meta: the row for this model, plus any kit item that itself
    // is a whole-word match — rare, since a real kit name is almost always a
    // compound the word-boundary rule leaves alone, and that is correct.
    for (const meta of metaFilesOfKind(input, "carvariations")) {
      const cursor = { at: 0 };
      for (const row of parseCarvariationsMeta(meta.doc)) {
        const to = plan.get(row.model);
        if (to === undefined || to === null) continue;
        const raw = directChildText(row.element, "modelName");
        edits.push(
          ...fieldEdits(qualify(meta.path), meta.source, "modelName", raw, cursor, row.model, to),
        );
        for (const kit of row.kits) {
          if (wordSpans(kit, row.model).length === 0) continue;
          const offset = tagValueOffset(meta.source, "Item", kit, cursor.at);
          if (offset < 0) continue;
          for (const span of wordSpans(kit, row.model)) {
            const start = offset + span.start;
            const end = offset + span.end;
            edits.push(
              textEdit(qualify(meta.path), start, end, meta.source.slice(start, end), to),
            );
          }
        }
      }
    }

    // carcols.meta: kits are global to the file, not per-vehicle, so every
    // requested code is tested against every kit name — still one *known
    // field*, never "every occurrence in the file".
    for (const meta of metaFilesOfKind(input, "carcols")) {
      const cursor = { at: 0 };
      for (const kit of parseCarcolsMeta(meta.doc).kits) {
        for (const [oldCode, to] of plan) {
          if (to === null) continue;
          if (wordSpans(kit.kitName, oldCode).length === 0) continue;
          edits.push(
            ...fieldEdits(qualify(meta.path), meta.source, "kitName", kit.kitName, cursor, oldCode, to),
          );
        }
      }
    }

    // Lua/SQL tables that name a vehicle by its spawn code as a bare word.
    for (const [path, source] of input.texts) {
      const name = baseName(path).toLowerCase();
      const isFrameworkTable =
        name === VEHICLE_NAMES_LUA ||
        normalizePath(path).toLowerCase().endsWith(QBCORE_VEHICLES_SUFFIX) ||
        name === ESX_VEHICLES_SQL;
      if (!isFrameworkTable) continue;
      for (const [oldCode, to] of plan) {
        if (to === null) continue;
        const spans = wordSpans(source, oldCode);
        if (spans.length > 0) matchedOld.add(oldCode);
        for (const span of spans) {
          edits.push(
            textEdit(qualify(path), span.start, span.end, source.slice(span.start, span.end), to),
          );
        }
      }
    }

    // ── File renames: streamed companions, their high-LOD partners, the audio
    // banks whose declared stem literally spells the code, and the
    // `sfx/dlc_<code>/` folder convention.
    //
    // ONE pass over the files, producing at most ONE target path per source, and
    // that is not a style preference. These three conventions overlap on real
    // packs — `sfx/dlc_20ambocr/20ambocr.awc` is a stream extension living inside
    // a `dlc_<code>` folder — and computing them in two separate passes produced
    // two `fileRenames` entries with the same `from` and different `to`. A caller
    // building an export turns `fileRenames` into a `Map` keyed by `from`, so the
    // second entry silently replaced the first and half the rename was lost with
    // no warning. Directory segments and the basename are therefore renamed
    // together, into a single destination.
    const streamExtensions = new Set(STREAM_EXTENSIONS);
    for (const file of input.files ?? []) {
      if (!file || typeof file.path !== "string") continue;
      const path = normalizePath(file.path);
      const extension = extensionOf(path);
      const segments = path.split("/");
      const last = segments.length - 1;
      let changed = false;

      // Directory segments: the `sfx/dlc_<code>/` convention. Only directories —
      // a file's own basename is handled below, by extension, so a `dlc_<code>`
      // *file* is never caught by both rules.
      const dirSegments = segments.slice(0, last).map((segment) => {
        for (const [oldCode, to] of plan) {
          if (to === null) continue;
          if (normalizeName(segment) !== `dlc_${oldCode}`) continue;
          changed = true;
          matchedOld.add(oldCode);
          return `dlc_${to}`;
        }
        return segment;
      });

      // The basename.
      let fileName = segments[last] ?? "";
      if (streamExtensions.has(extension)) {
        const hi = isHighLodName(path);
        const stem = normalizeName(hi ? stripHighLod(stemOf(path)) : stemOf(path));
        const to = plan.get(stem);
        if (to !== undefined && to !== null) {
          const newStem = hi ? highLodNames(to, extension)[0].replace(`.${extension}`, "") : to;
          fileName = `${newStem}.${extension}`;
          changed = true;
          matchedOld.add(stem);
        }
      } else if (AUDIO_BANK_EXTENSIONS.has(extension)) {
        // `audioconfig/20ambocr.dat151.rel` is the ordinary spelling, and the
        // brief names this file type by name. The *contents* stay compiled and
        // unread — see the note this pass pushes below — but the file name is a
        // path, which is exactly what a `{from, to}` pair can safely describe.
        //
        // Whole-word, through the same `wordSpans` rule every field in this
        // module uses, and for the same reason: `dlcambo_game.dat` has no word
        // boundary around `20ambocr` and is left alone, while `20ambocr.dat151`
        // does (a `.` is a boundary) and is renamed.
        const stem = stemOf(path);
        for (const [oldCode, to] of plan) {
          if (to === null) continue;
          if (wordSpans(stem, oldCode).length === 0) continue;
          const pattern = new RegExp(`\\b${escapeRegExp(oldCode)}\\b`, "gi");
          fileName = `${stem.replace(pattern, to)}.${extension}`;
          changed = true;
          matchedOld.add(oldCode);
          break;
        }
      }

      if (!changed) continue;
      const dir = dirSegments.length > 0 ? `${dirSegments.join("/")}/` : "";
      fileRenames.push({ from: qualify(path), to: qualify(`${dir}${fileName}`) });
    }

    // fxmanifest.lua / __resource.lua: keep a declared path in sync with a file
    // this batch just renamed, so a meta that streams today keeps streaming.
    const manifestPath = MANIFEST_FILE_NAMES.find((name) => input.texts.has(name));
    if (manifestPath) {
      const source = input.texts.get(manifestPath) ?? "";
      const localRenames = fileRenames.filter((r) => r.from.startsWith(`${qualify("")}`));
      for (const { from, to } of localRenames) {
        const oldRelative = from.slice(qualify("").length);
        const newRelative = to.slice(qualify("").length);
        const pattern = new RegExp(`(['"])${escapeRegExp(oldRelative)}\\1`, "g");
        let match: RegExpExecArray | null;
        while ((match = pattern.exec(source)) !== null) {
          const start = match.index + 1; // inside the quotes
          const end = start + oldRelative.length;
          edits.push(
            textEdit(qualify(manifestPath), start, end, source.slice(start, end), newRelative),
          );
        }
      }
    }

    // Honesty about what this pass did not open.
    const audioBanks = (input.files ?? []).filter(
      (f) => f && typeof f.path === "string" && AUDIO_BANK_EXTENSIONS.has(extensionOf(f.path)),
    );
    if (audioBanks.length > 0) {
      // Counted against the renames this pass actually produced, not against
      // what the convention suggests. The earlier wording here claimed file
      // names "were only renamed when a name matched a requested code exactly"
      // at a time when no code path renamed an audio bank at all — a user who
      // read it concluded a matching bank had been handled and shipped a pack
      // with silently mismatched audio. A note that states a rule the code does
      // not follow is worse than no note, so this one counts.
      const renamedBanks = audioBanks.filter((f) =>
        fileRenames.some((rename) => rename.from === qualify(normalizePath(f.path))),
      ).length;
      const untouched = audioBanks.length - renamedBanks;
      notes.push(
        `${labelOf.get(normalizeName(identity)) ?? input.name}: ` +
          `${audioBanks.length} audio bank file${audioBanks.length === 1 ? "" : "s"} ` +
          `(${audioBanks.slice(0, 3).map((f) => f.path).join(", ")}) ` +
          `${audioBanks.length === 1 ? "was" : "were"} not opened: the contents are compiled binary ` +
          "this tool cannot parse, so a game name or a bank name recorded *inside* one is unchanged. " +
          `${renamedBanks} of them had a file name that spells a requested code as a whole word and ` +
          `${renamedBanks === 1 ? "was" : "were"} renamed; ` +
          `${untouched === 0 ? "none were left" : `the other ${untouched} ${untouched === 1 ? "was" : "were"} left`} ` +
          "alone. Renaming the file does not re-point the vehicle's `audioNameHash` at a new bank — " +
          "check the audio by hand either way.",
      );
    }
    const gxt2 = (input.files ?? []).filter(
      (f) => f && typeof f.path === "string" && extensionOf(f.path) === "gxt2",
    );
    if (gxt2.length > 0) {
      notes.push(
        `${labelOf.get(normalizeName(identity)) ?? input.name}: ${gxt2.length} .gxt2 ` +
          `file${gxt2.length === 1 ? "" : "s"} (${gxt2.slice(0, 3).map((f) => f.path).join(", ")}) ` +
          `${gxt2.length === 1 ? "was" : "were"} neither opened nor renamed — a .gxt2 is compiled text, ` +
          "not something this tool's text-based patcher can safely edit, and it is named after a " +
          "language rather than a vehicle. If a display label lives in one, update it by hand.",
      );
    }
  }

  // ── The full output path set ───────────────────────────────────────────────
  //
  // The occupancy map above answers "would two things end up called the same
  // *name*", which it can only do for the two things that carry a name this
  // module knows how to read: a `vehicles.meta` `<modelName>`, and a `.yft`/
  // `.ytd` stem. That is two of the thirteen extensions the rename pass above
  // actually moves, and none of the `sfx/dlc_<code>/` folder convention.
  //
  // So the name check is not the collision check, and the brief is explicit
  // about which one is: "compute the full output path set before doing
  // anything, and stop if two sources resolve to one target". This is that. It
  // walks every file every resource holds, maps it through the renames this plan
  // produced, and refuses the batch if two distinct sources land on one path —
  // which is the only check that covers `.ytyp`, `.ymap`, `.awc` and the rest,
  // and the only one that sees a `dlc_<old>` folder merging into an existing
  // `dlc_<new>` one.
  //
  // It has to run here, after the per-resource pass, because `fileRenames` does
  // not exist until then. That ordering is safe for the reason the occupancy map
  // was built early: nothing between here and there has *written* anything.
  //
  // A two-way swap still passes, and not by a special case — `a.yft -> b.yft`
  // and `b.yft -> a.yft` are two sources resolving to two different targets.
  const renameTargets = new Map(fileRenames.map((rename) => [rename.from, rename.to]));
  const outputPaths = new Map<string, string[]>();
  for (const input of all) {
    const identity = input.identity ?? input.name;
    const label = labelOf.get(normalizeName(identity)) ?? input.name;
    for (const file of input.files ?? []) {
      if (!file || typeof file.path !== "string") continue;
      const from = qualifyPath(identity, normalizePath(file.path));
      const to = renameTargets.get(from) ?? from;
      const source =
        to === from
          ? `${label}'s existing ${normalizePath(file.path)}`
          : `${label}'s ${normalizePath(file.path)}, renamed`;
      outputPaths.set(to, [...(outputPaths.get(to) ?? []), source]);
    }
  }
  for (const [target, sources] of outputPaths) {
    if (sources.length < 2) continue;
    const unique = [...new Set(sources)];
    collisions.push({ target, sources: unique });
    warnings.push({
      code: "stream.path.duplicate",
      severity: "error",
      title: `Two files would both be written to "${target}"`,
      detail:
        `${unique.join(" and ")} would both end up at "${target}" if this batch ran. ` +
        "One would overwrite the other and the export would still report success, which is the " +
        "exact failure this tool exists to prevent, so the whole rename was refused before " +
        "anything was written. Rename to a name no file in this call already occupies, or move " +
        "the file that is in the way first.",
      entity: { kind: "stream_asset", id: target },
    });
  }

  const unreferenced = [...plan.keys()]
    .filter((code) => !matchedOld.has(code) && plan.get(code) !== null)
    .map((code) => rawOldByNormalized.get(code) ?? code);

  const blocked = collisions.length > 0 || invalid.length > 0;
  const finalFileRenames = blocked ? [] : dedupeFileRenames(fileRenames);
  const finalEdits = blocked ? [] : edits;

  return {
    fileRenames: finalFileRenames,
    edits: finalEdits,
    collisions,
    unreferenced,
    warnings,
    invalid,
    notes: [...new Set(notes)],
    blocked,
    renamed,
  };
}

/** `fileRenames` built per file-kind pass above can repeat an identical entry; collapse it. */
function dedupeFileRenames(
  fileRenames: readonly { from: string; to: string }[],
): { from: string; to: string }[] {
  const seen = new Set<string>();
  const out: { from: string; to: string }[] = [];
  for (const entry of fileRenames) {
    const key = `${entry.from} ${entry.to}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(entry);
  }
  return out;
}

// ─── Applying a plan ─────────────────────────────────────────────────────────

export type RenameApplyResult = {
  /** Qualified file path -> its text after every verified edit for that file. */
  files: Map<string, string>;
  applied: GtaTextEdit[];
  rejected: { edit: GtaTextEdit; reason: string }[];
};

/**
 * Run `plan.edits` through `patch.ts`'s verified apply, grouped by file.
 *
 * `texts` is keyed by the same resource-qualified paths `plan.edits[].file`
 * uses. Running this twice against the same starting texts is safe by
 * construction and not by anything special written here: the second call's
 * edits carry the *old* text as their expectation, `verifyEdit` finds the new
 * text sitting there instead, and every one of them is rejected rather than
 * applied a second time. That is `patch.ts`'s guarantee, reused rather than
 * reimplemented.
 */
export function applyRenamePlan(
  plan: RenamePlan,
  texts: ReadonlyMap<string, string>,
): RenameApplyResult {
  const byFile = new Map<string, GtaTextEdit[]>();
  for (const edit of plan.edits) {
    byFile.set(edit.file, [...(byFile.get(edit.file) ?? []), edit]);
  }

  const files = new Map<string, string>();
  const applied: GtaTextEdit[] = [];
  const rejected: { edit: GtaTextEdit; reason: string }[] = [];

  for (const [file, fileEdits] of byFile) {
    const source = texts.get(file) ?? "";
    const result = applyEdits(source, fileEdits);
    files.set(file, result.text);
    applied.push(...result.applied);
    rejected.push(...result.rejected);
  }

  return { files, applied, rejected };
}

// ─── Two-phase file renaming, for an in-place write ─────────────────────────

export type RenameStep = { from: string; to: string };

/**
 * Turn a set of file renames into a sequence of steps that never collides,
 * even when two codes swap.
 *
 * Applying `fileRenames` directly, in order, is only safe when no `to` is also
 * some other entry's `from` — the moment two codes swap, the direct rename of
 * the first pair overwrites the file the second pair still needs to read. So
 * every rename here goes to a unique temporary name first, and only once every
 * source has been moved out of the way does the second phase move every file to
 * its real destination. A caller doing an in-place write (File System Access,
 * or a desktop folder) executes the returned steps *in order*; a caller writing
 * a fresh zip does not need this at all, because a zip is built additively and
 * two entries never contend for one name on disk mid-write.
 */
export function twoPhaseRenameSteps(
  fileRenames: readonly { from: string; to: string }[],
): RenameStep[] {
  const tempOf = (path: string, index: number): string => `${path}.__workshop_rename_${index}__`;
  const phase1: RenameStep[] = fileRenames.map((entry, index) => ({
    from: entry.from,
    to: tempOf(entry.from, index),
  }));
  const phase2: RenameStep[] = fileRenames.map((entry, index) => ({
    from: tempOf(entry.from, index),
    to: entry.to,
  }));
  return [...phase1, ...phase2];
}
